import { Link } from "react-router-dom";
import {
    IdentificationCard,
    CursorClick,
    Lightning,
    FileArrowDown,
    Printer,
    UsersThree,
    CheckCircle,
    ArrowRight,
    DownloadSimple,
} from "@phosphor-icons/react";
import useSeo from "../hooks/useSeo";

const HERO =
    "https://static.prod-images.emergentagent.com/jobs/f4bf2fe5-a8dd-4638-a1bf-400fe11faae7/images/e6539e8dee16eddd9751e16bb27aa6e396568fc3e5792cd6491769bfae55f3f6.png";
const TEMPLATES =
    "https://static.prod-images.emergentagent.com/jobs/f4bf2fe5-a8dd-4638-a1bf-400fe11faae7/images/a4fc70e2216aa51477a45366e66c7cbac9168032219ab00700a69fe640a7f29b.png";

function Feature({ icon: Icon, title, desc, g }) {
    return (
        <div className="card-surface p-6 hover:-translate-y-1 hover:shadow-md group">
            <div
                className={`w-11 h-11 ${g} text-white flex items-center justify-center mb-4 shadow-lg`}
            >
                <Icon size={22} weight="duotone" />
            </div>
            <h3 className="font-display font-black text-xl mb-1.5 tracking-tight">
                {title}
            </h3>
            <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
        </div>
    );
}

export default function Landing() {
    useSeo({
        title: "IDCardGen — Generate Smart ID Cards Instantly",
        description:
            "IDCardGen lets schools and institutions design, bulk-import and print professional ID cards in minutes. Free trial, Excel import, drag-and-drop editor, PDF/PNG export.",
        canonical: "https://idcardgen.com/",
        keywords:
            "id card generator, school id card maker, employee id card, bulk id card maker, print id cards, id card design online, idcardgen",
    });

    return (
        <div data-testid="landing-page">
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-zinc-200 bg-mesh-1">
                <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-5 gap-12 items-center">
                    <div className="lg:col-span-3">
                        <div className="inline-flex items-center gap-2 border border-violet-300 bg-white px-3 py-1 text-xs font-mono uppercase tracking-widest mb-6 shadow-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-violet-600 to-pink-500"></span>
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-700 to-pink-700 font-semibold">
                                Built for Schools · Colleges · Offices
                            </span>
                        </div>
                        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
                            Design &amp; print
                            <br />
                            ID cards in
                            <br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 via-blue-600 to-pink-600">
                                minutes.
                            </span>
                        </h1>
                        <p className="mt-6 text-lg text-zinc-600 max-w-xl leading-relaxed">
                            A modern drag-and-drop studio for institutional
                            identity cards. Bulk-import students from Excel, map
                            fields, export PDF — or send straight to the
                            printer.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/register"
                                className="btn-gradient flex items-center gap-2 shadow-glow-violet"
                                data-testid="hero-cta-register"
                            >
                                Start Free{" "}
                                <ArrowRight size={16} weight="bold" />
                            </Link>
                            <Link
                                to="/pricing"
                                className="btn-secondary"
                                data-testid="hero-cta-pricing"
                            >
                                View Pricing
                            </Link>
                            <a
                                href="/samples/idcardgen-sample.pdf"
                                download
                                className="btn-ghost flex items-center gap-2"
                                data-testid="hero-sample-pdf"
                                onClick={() => {
                                    if (typeof window?.gtag === "function") {
                                        window.gtag("event", "download", {
                                            file_name: "sample_pdf",
                                            page: "landing",
                                        });
                                    }
                                }}
                            >
                                <DownloadSimple size={16} weight="bold" />{" "}
                                Sample PDF
                            </a>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2 text-xs">
                            <Link
                                to="/id-card/school"
                                className="underline-offset-2 hover:underline text-violet-700 font-semibold"
                            >
                                School ID cards
                            </Link>
                            <span className="text-zinc-300">·</span>
                            <Link
                                to="/id-card/college"
                                className="underline-offset-2 hover:underline text-violet-700 font-semibold"
                            >
                                College ID cards
                            </Link>
                            <span className="text-zinc-300">·</span>
                            <Link
                                to="/id-card/employee"
                                className="underline-offset-2 hover:underline text-violet-700 font-semibold"
                            >
                                Employee ID cards
                            </Link>
                        </div>
                        <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-zinc-500 font-mono uppercase tracking-widest">
                            <span className="flex items-center gap-1.5">
                                <CheckCircle
                                    size={14}
                                    color="#16A34A"
                                    weight="fill"
                                />{" "}
                                Excel Import
                            </span>
                            <span className="flex items-center gap-1.5">
                                <CheckCircle
                                    size={14}
                                    color="#16A34A"
                                    weight="fill"
                                />{" "}
                                Front + Back
                            </span>
                            <span className="flex items-center gap-1.5">
                                <CheckCircle
                                    size={14}
                                    color="#16A34A"
                                    weight="fill"
                                />{" "}
                                PDF / PNG / Print
                            </span>
                        </div>
                    </div>
                    <div className="lg:col-span-2 relative">
                        <div className="absolute -inset-4 g-violet opacity-20 blur-2xl -z-10" />
                        <img
                            src={HERO}
                            alt="IDCardGen preview"
                            className="w-full border border-zinc-900 shadow-2xl"
                        />
                        <div className="mt-4 g-pink text-white px-4 py-3 flex items-center justify-between shadow-glow-pink">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-white/80">
                                    Now live
                                </div>
                                <div className="font-display font-bold">
                                    Bulk Excel → 500 cards in 30s
                                </div>
                            </div>
                            <Lightning
                                size={28}
                                weight="fill"
                                color="#FFF5B7"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats strip */}
            <section className="bg-mesh-dark text-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 relative z-10">
                    {[
                        [
                            "86×54 mm",
                            "CR80 Standard",
                            "from-violet-400 to-blue-400",
                        ],
                        [
                            "2-sided",
                            "Front & back support",
                            "from-blue-400 to-cyan-400",
                        ],
                        [
                            "Unlimited",
                            "Templates per user",
                            "from-pink-400 to-amber-400",
                        ],
                        [
                            "300 DPI",
                            "Print-ready export",
                            "from-emerald-400 to-cyan-400",
                        ],
                    ].map(([a, b, g]) => (
                        <div key={a} className="border-l border-white/10 pl-4">
                            <div
                                className={`font-display font-black text-3xl tracking-tighter bg-clip-text text-transparent bg-gradient-to-r ${g}`}
                            >
                                {a}
                            </div>
                            <div className="text-xs uppercase tracking-widest text-zinc-400 mt-1">
                                {b}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Features */}
            <section className="max-w-7xl mx-auto px-6 py-24">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-blue-600 mb-2">
                            Capabilities
                        </div>
                        <h2 className="font-display font-black text-4xl tracking-tighter">
                            Everything you need to ship ID cards.
                        </h2>
                    </div>
                    <Link
                        to="/register"
                        className="btn-ghost flex items-center gap-1 text-blue-600"
                    >
                        Try the studio <ArrowRight size={14} />
                    </Link>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    <Feature
                        g="g-violet"
                        icon={CursorClick}
                        title="Drag-drop designer"
                        desc="Snap-to-grid canvas with text, images, photos, shapes, QR codes and barcodes. Full front and back layouts."
                    />
                    <Feature
                        g="g-ocean"
                        icon={FileArrowDown}
                        title="Bulk Excel import"
                        desc="Upload your student sheet. Map columns once. Every row becomes a unique, generated card."
                    />
                    <Feature
                        g="g-sunset"
                        icon={Printer}
                        title="Print &amp; export"
                        desc="Download PDF or PNG, or send cards directly to any CR80 ID printer with one click."
                    />
                    <Feature
                        g="g-emerald"
                        icon={UsersThree}
                        title="Team &amp; roles"
                        desc="Admin approves payments, manages packages & users. Regular users only see their own projects."
                    />
                    <Feature
                        g="g-pink"
                        icon={Lightning}
                        title="Credits system"
                        desc="Purchase packages → admin approves → credits unlock. Clean, transparent usage tracking."
                    />
                    <Feature
                        g="g-amber"
                        icon={IdentificationCard}
                        title="Template library"
                        desc="Save and reuse your designs. Duplicate, tweak and roll out variations instantly."
                    />
                </div>
            </section>

            {/* Template showcase */}
            <section className="bg-zinc-100 border-y border-zinc-200">
                <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-blue-600 mb-2">
                            Templates
                        </div>
                        <h2 className="font-display font-black text-4xl tracking-tighter mb-4">
                            Start from a template. Or build from scratch.
                        </h2>
                        <p className="text-zinc-600 leading-relaxed mb-6">
                            Modern, Swiss-inspired ID card templates for
                            schools, universities and corporate offices. Brand
                            them with your logo, colours and student photos in
                            minutes.
                        </p>
                        <Link
                            to="/register"
                            className="btn-primary inline-flex items-center gap-2"
                        >
                            Open the studio{" "}
                            <ArrowRight size={16} weight="bold" />
                        </Link>
                    </div>
                    <img
                        src={TEMPLATES}
                        alt="ID card templates"
                        className="w-full border border-zinc-900 shadow-xl"
                    />
                </div>
            </section>

            {/* CTA */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-mesh-1" />
                <div className="relative max-w-7xl mx-auto px-6 py-24 text-center">
                    <h2 className="font-display font-black text-5xl tracking-tighter mb-4">
                        Ready in the next{" "}
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 via-pink-500 to-amber-500">
                            5 minutes.
                        </span>
                    </h2>
                    <p className="text-zinc-600 max-w-xl mx-auto mb-8">
                        Register, pick a package, and our admin verifies your
                        payment. Then start designing.
                    </p>
                    <div className="flex justify-center gap-3">
                        <Link
                            to="/register"
                            className="btn-gradient shadow-glow-violet"
                        >
                            Create Account
                        </Link>
                        <Link to="/pricing" className="btn-secondary">
                            See Packages
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-zinc-200 py-10 text-center text-xs text-zinc-500 font-mono uppercase tracking-widest">
                <div className="max-w-3xl mx-auto px-6 space-y-2">
                    <div>
                        © {new Date().getFullYear()} IDCardGen — Built for
                        modern institutions
                    </div>
                    <div className="normal-case tracking-normal text-zinc-600">
                        Contact:{" "}
                        <a
                            href="mailto:contact@idcardgen.com"
                            className="text-blue-600 hover:underline font-semibold"
                        >
                            contact@idcardgen.com
                        </a>{" "}
                        · Web:{" "}
                        <a
                            href="https://idcardgen.com"
                            className="text-blue-600 hover:underline font-semibold"
                        >
                            idcardgen.com
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
